import { Col, ConfirmModal, Flex, Row, Text, TitledText } from '@/components';
import { accountingType } from '@/features/master-table/models';
import { useProject, useUpdateRemark } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import type {
  ExpenditureCartModel,
  ExpenditureCartsModel,
} from '@/features/project/projects/_models/expenditure-cart';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { formatValueByUnit } from '@/utils';
import classNames from 'classnames';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { BelanjaProgramTable } from './BelanjaProgramTable';
import { BiayaProgramTable } from './BiayaProgramTable';
import styles from './ExpenditureCartsTable.module.scss';

type Props = {
  data: z.infer<typeof ExpenditureCartsModel>;
  projectId: string;
  subProjectId: string;
  onClickDelete: (value: z.infer<typeof ExpenditureCartModel>) => void;
  editable: boolean;
};

const _tableStyles = classNames(
  styles.ant_table_content,
  styles.ant_table_container,
  styles.ant_row,
);
const _tableHeaderStyle = {
  fontSize: 12,
};

const _PREVIEW_ROW: z.infer<typeof ExpenditureCartModel> = {
  id: PREVIEW_ROW_ID,
  createdAt: '',
  updatedAt: '',
  priorId: null,
  subProjectId: '',
  accountingId: '',
  accountTitleId: null,
  reasonForChange: null,
  remark: null,
  desiredAmount: null,
  accountingType: {
    id: '',
    code: 1,
    type: accountingType.options[0],
    name: '',
    startYear: null,
    endYear: null,
    createdAt: '',
    updatedAt: '',
  },
  accountTitle: null,
  expenditureItems: [],
  executionItem: null,
};

export const RsExpenditureCartsTable: FC<Props> = ({
  onClickDelete: _onClickDelete,
  editable: _editable,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id } = useId();
  const { mutateAsync: updateRemarkAsync } = useUpdateRemark();
  const [expenditureItemDelete, setExpenditureItemDelete] = useState<{
    id: string;
    type: 'Biaya' | 'Belanja';
  } | null>(null);

  const project = useProject(id);

  const fiscalYear = project.data?.fiscalYear ?? 0;

  const belanjaProgram = project.data?.indonesiaData?.belanjaProgram ?? [];
  const biayaTenaga = project.data?.indonesiaData?.biayaTenaga ?? [];

  return (
    <>
      <Flex vertical gap={16}>
        <Text variant="h4" text={t('belanja-program')} />
        <BelanjaProgramTable
          fiscalYear={fiscalYear}
          data={belanjaProgram}
          onClickDelete={(value) => {
            setExpenditureItemDelete(value);
          }}
        />
        <TitledText
          titleVariant="h4"
          titleColor="normal"
          title={t('increase-decrease-main-reason')}
          text={project.data?.increaseDecreaseMainReason}
        />
        <Text variant="h4" text={t('biaya-tenaga')} />
        <BiayaProgramTable
          fiscalYear={fiscalYear}
          data={biayaTenaga}
          onClickDelete={(value) => {
            setExpenditureItemDelete(value);
          }}
        />

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <TitledText
              titleVariant="h4"
              titleColor="normal"
              title={fiscalYear + t('total-biaya-program')}
              text={formatValueByUnit(
                project.data?.indonesiaData?.totalCurrentYearBiayaTenaga ?? 0,
              )}
            />
          </Col>
          <Col span={12}>
            <TitledText
              titleVariant="h4"
              titleColor="normal"
              title={fiscalYear - 1 + t('total-biaya-program')}
              text={formatValueByUnit(
                project.data?.indonesiaData?.totalPreviousYearBiayaTenaga ?? 0,
              )}
            />
          </Col>
          <Col span={12}>
            <TitledText
              titleVariant="h4"
              titleColor="normal"
              title={t('revenue-source')}
              text={project.data?.privateReason}
            />
          </Col>
          <Col span={12}>
            <TitledText
              titleVariant="h4"
              titleColor="normal"
              title={t('other-special-notes')}
              text={project.data?.otherSpecialNotes}
            />
          </Col>
        </Row>
      </Flex>
      <ConfirmModal
        open={expenditureItemDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: t('expenditure-item'),
        })}
        onOk={async () => {
          invariant(expenditureItemDelete, t('unreachable-code'));

          try {
            await updateRemarkAsync({
              id,
              indonesiaData: {
                ...project.data?.indonesiaData,
                belanjaProgram:
                  expenditureItemDelete.type === 'Belanja'
                    ? project.data?.indonesiaData?.belanjaProgram?.filter(
                        (v) => v.id !== expenditureItemDelete.id,
                      )
                    : project.data?.indonesiaData?.belanjaProgram,
                biayaTenaga:
                  expenditureItemDelete.type === 'Biaya'
                    ? project.data?.indonesiaData?.biayaTenaga?.filter(
                        (v) => v.id !== expenditureItemDelete.id,
                      )
                    : project.data?.indonesiaData?.biayaTenaga,
              },
            });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureItemDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setExpenditureItemDelete(null);
        }}
        skipCheck
      />
    </>
  );
};
